import { useState, useEffect } from "react";
import useBaskets from "../../data/useBaskets";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import { IBasketItem } from "../../models/Basket";
import { FormatType } from "../../models/EbayOffer";
import PriceFormat from "../../utils/priceFormat";
import { MAX_PART_PRICE } from "../../utils/priceLimits";

export default function BasketPriceEditor({ basketItem }: { basketItem: IBasketItem }) {
  const { updateItem } = useBaskets("BasketPriceEditor");
  const { saveHandlers } = useDefaultCRUDHandlers("Basket Item");
  const { addNotification } = useNotification();
  const [editPrice, setEditPrice] = useState<string>("");

  const getDefaultPrice = () => {
    if (!basketItem.part.offers || basketItem.part.offers.length === 0) {
      return basketItem.part.enteredPrice || 0;
    }

    const offer = basketItem.part.offers[0];
    if (offer.format === FormatType.FixedPrice) {
      return offer.buyItNowPrice || 0;
    } else {
      return offer.auctionStartPrice || 0;
    }
  };

  const getCurrentPrice = () => {
    return basketItem.basketPrice ?? getDefaultPrice();
  };

  // Initialize editPrice with current price when component mounts or basketItem changes
  useEffect(() => {
    setEditPrice(getCurrentPrice().toString());
  }, [basketItem]);

  const handlePriceUpdate = () => {
    const newPrice = parseFloat(editPrice);
    
    if (isNaN(newPrice) || newPrice < 0) {
      addNotification({
        primaryText: "Invalid price entered",
        variant: "warn"
      });
      setEditPrice(getCurrentPrice().toString()); // Reset to current price
      return;
    }

    if (newPrice > MAX_PART_PRICE) {
      addNotification({
        primaryText: `Price cannot exceed ${PriceFormat(MAX_PART_PRICE)}`,
        variant: "warn"
      });
      setEditPrice(getCurrentPrice().toString()); // Reset to current price
      return;
    }

    updateItem.mutate({
      ...basketItem,
      basketPrice: newPrice
    }, saveHandlers);
  };

  return (
    <div className="flex flex-col items-center gap-y-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-x-2">
        <span>£</span>
        <input
          value={editPrice}
          onChange={(e) => setEditPrice(e.target.value)}
          onBlur={handlePriceUpdate}
          step={0.01}
          min={0}
          max={MAX_PART_PRICE}
          className="h-8 w-20 border p-1 text-sm"
          type="number"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handlePriceUpdate();
            } else if (e.key === "Escape") {
              setEditPrice(getCurrentPrice().toString()); // Reset to current price
            }
          }}
        />
      </div>
    </div>
  );
}
